/**
 * Tab Veterinari nell'Admin Panel.
 * CRUD veterinari con selezione specialità.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { vetsApi, Vet, VetCreate, VetUpdate } from '../../api/vets'
import { specialtiesApi, Specialty } from '../../api/specialties'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Pencil, AlertTriangle } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function VetsTab() {
  const [showVetForm, setShowVetForm] = useState(false)
  const [editingVet, setEditingVet] = useState<Vet | null>(null)
  const [vetForm, setVetForm] = useState<VetCreate>({ full_name: '', email: '', specialties: [] })
  const [deactivateError, setDeactivateError] = useState<{ message: string; open_cases_count: number; open_cases: any[] } | null>(null)

  const queryClient = useQueryClient()

  const { data: vets } = useQuery<Vet[]>({
    queryKey: ['vets'],
    queryFn: () => vetsApi.list(false),
  })

  const { data: activeSpecialties } = useQuery<Specialty[]>({
    queryKey: ['specialties-active'],
    queryFn: () => specialtiesApi.listActive(),
    staleTime: 5 * 60 * 1000,
  })

  const createVetMutation = useMutation({
    mutationFn: vetsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vets'] })
      setShowVetForm(false)
      setVetForm({ full_name: '', email: '', specialties: [] })
    },
  })

  const updateVetMutation = useMutation({
    mutationFn: ({ vetId, data }: { vetId: string; data: VetUpdate }) => vetsApi.update(vetId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vets'] })
      setEditingVet(null)
      setVetForm({ full_name: '', email: '', specialties: [] })
    },
  })

  const deactivateVetMutation = useMutation({
    mutationFn: vetsApi.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vets'] })
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.open_cases_count) {
        setDeactivateError(detail)
      } else {
        alert(detail || 'Errore durante la disattivazione')
      }
    },
  })

  const activateVetMutation = useMutation({
    mutationFn: vetsApi.activate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vets'] })
    },
  })

  const handleToggleSpecialty = (spec: string) => {
    if (vetForm.specialties.includes(spec)) {
      setVetForm({ ...vetForm, specialties: vetForm.specialties.filter(s => s !== spec) })
    } else {
      setVetForm({ ...vetForm, specialties: [...vetForm.specialties, spec] })
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingVet) {
      updateVetMutation.mutate({ vetId: editingVet.id, data: vetForm as VetUpdate })
    } else {
      createVetMutation.mutate(vetForm)
    }
  }

  const openEditForm = (vet: Vet) => {
    setEditingVet(vet)
    setVetForm({ full_name: vet.full_name, email: vet.email, specialties: vet.specialties || [] })
    setShowVetForm(false)
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Gestione Veterinari</h2>
        <Button onClick={() => {
          setShowVetForm(!showVetForm)
          setVetForm({ full_name: '', email: '', specialties: [] })
        }}>
          + Aggiungi Veterinario
        </Button>
      </div>

      {showVetForm && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-md font-semibold text-foreground mb-4">Nuovo Veterinario</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome completo</Label>
                  <Input
                    value={vetForm.full_name}
                    onChange={(e) => setVetForm({ ...vetForm, full_name: e.target.value })}
                    placeholder="Dr. Mario Rossi"
                    required
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={vetForm.email}
                    onChange={(e) => setVetForm({ ...vetForm, email: e.target.value })}
                    placeholder="mario.rossi@vetmail.it"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Specialità</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-border rounded-lg p-3">
                  {activeSpecialties?.map((spec) => (
                    <label
                      key={spec.key}
                      className="flex items-center gap-2 cursor-pointer py-1"
                    >
                      <Checkbox
                        checked={vetForm.specialties.includes(spec.key)}
                        onCheckedChange={() => handleToggleSpecialty(spec.key)}
                      />
                      <span className="text-sm">{spec.label}</span>
                    </label>
                  ))}
                </div>
                {vetForm.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {vetForm.specialties.map((spec) => {
                      const label = activeSpecialties?.find(s => s.key === spec)?.label || spec
                      return (
                        <Badge key={spec} className="gap-1">
                          {label}
                          <button
                            type="button"
                            onClick={() => handleToggleSpecialty(spec)}
                            className="text-primary-foreground/70 hover:text-primary-foreground"
                          >
                            ×
                          </button>
                        </Badge>
                      )
                    })}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={createVetMutation.isPending}>
                  {createVetMutation.isPending ? 'Salvataggio...' : 'Salva'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowVetForm(false); setVetForm({ full_name: '', email: '', specialties: [] }) }}>
                  Annulla
                </Button>
              </div>
              {createVetMutation.isError && (
                <p className="text-danger text-sm">
                  Errore: {(createVetMutation.error as any)?.response?.data?.detail || 'Impossibile creare il veterinario'}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      {editingVet && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-md font-semibold text-foreground mb-4">Modifica Veterinario</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome completo</Label>
                  <Input
                    value={vetForm.full_name}
                    onChange={(e) => setVetForm({ ...vetForm, full_name: e.target.value })}
                    placeholder="Dr. Mario Rossi"
                    required
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={vetForm.email}
                    onChange={(e) => setVetForm({ ...vetForm, email: e.target.value })}
                    placeholder="mario.rossi@vetmail.it"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Specialità</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-border rounded-lg p-3">
                  {activeSpecialties?.map((spec) => (
                    <label
                      key={spec.key}
                      className="flex items-center gap-2 cursor-pointer py-1"
                    >
                      <Checkbox
                        checked={vetForm.specialties.includes(spec.key)}
                        onCheckedChange={() => handleToggleSpecialty(spec.key)}
                      />
                      <span className="text-sm">{spec.label}</span>
                    </label>
                  ))}
                </div>
                {vetForm.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {vetForm.specialties.map((spec) => {
                      const label = activeSpecialties?.find(s => s.key === spec)?.label || spec
                      return (
                        <Badge key={spec} className="gap-1">
                          {label}
                          <button
                            type="button"
                            onClick={() => handleToggleSpecialty(spec)}
                            className="text-primary-foreground/70 hover:text-primary-foreground"
                          >
                            ×
                          </button>
                        </Badge>
                      )
                    })}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={updateVetMutation.isPending}>
                  {updateVetMutation.isPending ? 'Salvataggio...' : 'Aggiorna'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setEditingVet(null); setVetForm({ full_name: '', email: '', specialties: [] }) }}>
                  Annulla
                </Button>
              </div>
              {updateVetMutation.isError && (
                <p className="text-danger text-sm">
                  Errore: {(updateVetMutation.error as any)?.response?.data?.detail || 'Impossibile aggiornare il veterinario'}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Specialità</TableHead>
              <TableHead>Casi Aperti</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vets?.map((vet) => (
              <TableRow key={vet.id}>
                <TableCell className="font-medium">{vet.full_name}</TableCell>
                <TableCell>{vet.email}</TableCell>
                <TableCell>
                  {vet.specialties?.map(spec => {
                    const label = activeSpecialties?.find(s => s.key === spec)?.label || spec
                    return label
                  }).join(', ') || '-'}
                </TableCell>
                <TableCell>{vet.open_cases_count || 0}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    vet.is_active
                      ? 'bg-success/10 text-success border-success/20'
                      : 'bg-muted text-muted-foreground border-border'
                  }>
                    {vet.is_active ? 'Attivo' : 'Disattivato'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditForm(vet)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    {vet.is_active ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deactivateVetMutation.mutate(vet.id)}
                      >
                        Disattiva
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => activateVetMutation.mutate(vet.id)}
                      >
                        Attiva
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Deactivate Error Dialog */}
      <Dialog open={!!deactivateError} onOpenChange={(open) => !open && setDeactivateError(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="size-5" />
              Impossibile Disattivare
            </DialogTitle>
            <DialogDescription>
              {deactivateError?.message}
            </DialogDescription>
          </DialogHeader>
          {deactivateError?.open_cases && deactivateError.open_cases.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Casi aperti:</p>
              <div className="border border-border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID Caso</TableHead>
                      <TableHead>Titolo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deactivateError.open_cases.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono text-xs">{c.id.slice(0, 8)}...</TableCell>
                        <TableCell>{c.title || 'Senza titolo'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDeactivateError(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
